// AnalisisIngresosCostos.js
// 
// NOTA IMPORTANTE: Se detectó una discrepancia entre los totales calculados desde 
// resumen.totalDia vs la suma de líneas individuales (tablaPlana).
// Para mantener consistencia, tanto la pantalla como el Excel ahora calculan
// los totales sumando las líneas individuales en lugar de usar los subtotales precalculados.
//
import SoftBox from "components/SoftBox";
import { Card, Collapse, Icon, Chip, Box, Tabs, Tab, Paper, Divider, Grid, Tooltip, Modal, Backdrop, Fade, Autocomplete, TextField, CircularProgress } from "@mui/material";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import Table from "components/Tables/Table";
import DataTable from "components/Tables/DataTable";
import DataTableCustom from "components/Tables/DataTableCustom";
import { MenuContext } from "context/menuContext";
import dayjs from "dayjs";
import { useContext, useState, useEffect, useRef } from "react";
import { getUproColor } from "utils/colors";
import { restLink } from "services/config";
import { formatMoneyPunto } from "utils/formatters";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import PdfHeader from "components/PdfHeader";
import EditableText from "components/EditableText";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import IconButton from "@mui/material/IconButton";
import { keyframes } from "@mui/system";

const XLSX = require("xlsx");

// ----------------- Función segura para porcentajes -----------------
const formatPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00%";
  // Si el valor está entre -1.5 y 1.5, asumimos que es decimal (ej. 0.63 → 63%)
  const pct = Math.abs(n) <= 1.5 ? n * 100 : n;
  return `${pct.toFixed(2)}%`;
};

// Reemplazamos `pct` por `formatPercent` en todo el componente
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const str = (v) => (v === null || v === undefined ? "" : String(v));

function AnalisisIngresosCostos() {
  const { menu } = useContext(MenuContext);
  const [expandActive, setExpandActive] = useState(false);
  const [fechaOption, setFechaOption] = useState("hoy");
  const [fechaInicio, setFechaInicio] = useState(dayjs().startOf("day").toDate());
  const [fechaFin, setFechaFin] = useState(dayjs().endOf("day").toDate());
  const [loading, setLoading] = useState(false);
  const [reporteData, setReporteData] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [expandedDays, setExpandedDays] = useState({});
  const [expandedProducts, setExpandedProducts] = useState({});

  // Nuevo estado para categorías
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState("menuUniversitario");
  const [expandCategoriaActive, setExpandCategoriaActive] = useState(false);
  const [productoFiltroSeleccionado, setProductoFiltroSeleccionado] = useState(null);
  const [productoFiltroAplicado, setProductoFiltroAplicado] = useState(null);
  const [productosOptions, setProductosOptions] = useState([]);
  const [productosLoading, setProductosLoading] = useState(false);
  const [medioPagoSeleccionado, setMedioPagoSeleccionado] = useState("todos");
  const [expandMedioPagoActive, setExpandMedioPagoActive] = useState(false);

  // Estados para modales
  const [modalSegmentoOpen, setModalSegmentoOpen] = useState(false);
  const [modalProductoOpen, setModalProductoOpen] = useState(false);
  const [pedidosSegmento, setPedidosSegmento] = useState([]);
  const [pedidosProducto, setPedidosProducto] = useState([]);
  const [segmentoSeleccionado, setSegmentoSeleccionado] = useState("");
  const [productoSeleccionado, setProductoSeleccionado] = useState("");
  const [productoNombreSeleccionado, setProductoNombreSeleccionado] = useState("");
  const [productoIdSeleccionado, setProductoIdSeleccionado] = useState(null);
  const [segmentoProductoId, setSegmentoProductoId] = useState(null);
  const [segmentoCostoUnitSeleccionado, setSegmentoCostoUnitSeleccionado] = useState(null);
  const [productoCostoUnitSeleccionado, setProductoCostoUnitSeleccionado] = useState(null);
  const [loadingModal, setLoadingModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Ref para el contenido del PDF
  const pdfRef = useRef(null);

  // Estados para el PDF
  const [textArray, setTextArray] = useState([
    "Villa Mercedes (San Luis), " + dayjs().format("MMMM [de] YYYY") + ".",
    "ACUMULADO DE VENTAS",
    "Reporte de análisis de ingresos y costos",
    "Comedor Universitario UPrO",
    ".....................",
    ".....................",
    ".....................",
    ".....................",
    ".....................",
    ".....................",
    ".....................",
    ".....................",
    ".....................",
    ".....................",
    ".....................",
    ".....................",
    "DATOS ACUMULADOS",
  ]);

  const mediosDePagoOptions = [
    { id: "transferencia", nombre: "Transferencia" },
    { id: "cuentaCorriente", nombre: "Cuenta Corriente" },
    { id: "voucher", nombre: "Voucher" },
  ];

  // Mapeo de valores internos a nombres para mostrar
  const categoriasDisplay = {
    "menuUniversitario": "Menu Universitario",
    "cafeteria": "Cafetería",
    "uproDay": "UPrODay"
  };

  const fechaResumen = fechaOption === "personalizado"
    ? `${dayjs(fechaInicio).format("DD/MM/YYYY")} - ${dayjs(fechaFin).format("DD/MM/YYYY")}`
    : ({
      hoy: "Hoy",
      ayer: "Ayer",
      "Ultimos 7 días": "Ultimos 7 días",
      "Ultimos 30 días": "Ultimos 30 días",
    }[fechaOption] || fechaOption);

  // Cargar datos del reporte
  const fetchReporteData = async (productoOverride) => {
    setLoading(true);
    const productoParaConsulta = productoOverride !== undefined ? productoOverride : productoFiltroAplicado;

    let productoIdFiltro = null;
    if (
      productoParaConsulta &&
      productoParaConsulta.id !== undefined &&
      productoParaConsulta.id !== null
    ) {
      const parsedId = Number(productoParaConsulta.id);
      if (!Number.isNaN(parsedId)) {
        productoIdFiltro = parsedId;
      }
    }

    try {
      let url = `reportes/analisis-ingresos-costos?fechaInicio=${dayjs(fechaInicio).format(
        "YYYY-MM-DD"
      )}&fechaFin=${dayjs(fechaFin).format("YYYY-MM-DD")}&categoria=${categoriaSeleccionada}`;

      if (productoIdFiltro !== null) {
        url += `&idProducto=${productoIdFiltro}`;
      }

      if (medioPagoSeleccionado !== "todos") {
        url += `&medioPago=${encodeURIComponent(medioPagoSeleccionado)}`;
      }

      const response = await restLink.get(url);

      setReporteData(response.data);

      if (productoOverride !== undefined) {
        setProductoFiltroAplicado(productoParaConsulta || null);
      }
    } catch (error) {
      console.error("Error al cargar reporte:", error);
      setReporteData({ resumen: [], detalladoPorDia: {} });
    } finally {
      setLoading(false);
    }
  };


  const handleBuscar = () => {
    fetchReporteData(productoFiltroSeleccionado || null);
    setExpandedDays({})
  };

  const handleLimpiarProducto = () => {
    if (!productoFiltroSeleccionado && !productoFiltroAplicado) {
      return;
    }
    setExpandedDays({})
    setProductoFiltroSeleccionado(null);
    setProductoFiltroAplicado(null);
    fetchReporteData(null);
  };
  useEffect(() => {
    if (fechaOption !== "personalizado") {
      fetchReporteData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaOption, fechaInicio, fechaFin, categoriaSeleccionada, medioPagoSeleccionado]);

  useEffect(() => {
    let isMounted = true;

    const loadProductos = async () => {
      setProductosLoading(true);
      try {
        const response = await restLink.get(
          `reportes/analisis-ingresos-costos/productos?categoria=${categoriaSeleccionada}`
        );

        if (!isMounted) return;

        const productos = response?.data?.productos || [];
        setProductosOptions(productos);

        if (productoFiltroAplicado) {
          const match = productos.find((item) => item.id === productoFiltroAplicado.id);
          if (match) {
            setProductoFiltroAplicado(match);
            setProductoFiltroSeleccionado(match);
          } else {
            setProductoFiltroAplicado(null);
            setProductoFiltroSeleccionado(null);
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error al cargar productos para filtro:", error);
          setProductosOptions([]);
        }
      } finally {
        if (isMounted) {
          setProductosLoading(false);
        }
      }
    };

    loadProductos();

    return () => {
      isMounted = false;
    };
  }, [categoriaSeleccionada]);

  // Forzar pestaña Detallado cuando la categoría es cafetería
  useEffect(() => {
    if (categoriaSeleccionada === "cafeteria" && tabValue === 1) {
      setTabValue(0);
    }
  }, [categoriaSeleccionada, tabValue]);

  useEffect(() => {
    if (fechaOption === "personalizado") {
      fetchReporteData(productoFiltroAplicado || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriaSeleccionada, medioPagoSeleccionado]);

  // Función para cargar pedidos por segmento
  const fetchPedidosSegmento = async (segmento, idProducto = null, nombreProducto = null, costoUnit = null) => {
    // Abrir modal inmediatamente y configurar estados
    setSegmentoSeleccionado(segmento);
    setProductoNombreSeleccionado(nombreProducto || "");
    setSegmentoProductoId(idProducto || null);
    setSegmentoCostoUnitSeleccionado(costoUnit ?? null);
    setModalSegmentoOpen(true);
    setLoadingModal(true);
    setPedidosSegmento([]); // Limpiar datos anteriores

    try {
      let url = `reportes/detalle-pedidos-segmento?fechaInicio=${dayjs(fechaInicio).format(
        "YYYY-MM-DD"
      )}&fechaFin=${dayjs(fechaFin).format("YYYY-MM-DD")}&segmento=${encodeURIComponent(segmento)}&categoria=${categoriaSeleccionada}`;

      if (idProducto) {
        url += `&idProducto=${idProducto}`;
      }

      if (costoUnit !== null && costoUnit !== undefined) {
        url += `&costoUnit=${encodeURIComponent(costoUnit)}`;
      }

      if (medioPagoSeleccionado !== "todos") {
        url += `&medioPago=${encodeURIComponent(medioPagoSeleccionado)}`;
      }

      const response = await restLink.get(url);
      setPedidosSegmento(response.data.pedidos || []);
    } catch (error) {
      console.error("Error al cargar pedidos por segmento:", error);
      setPedidosSegmento([]);
    } finally {
      setLoadingModal(false);
    }
  };

  // Función para cargar pedidos por producto
  const fetchPedidosProducto = async (idProducto, nombreProducto, costoUnit = null) => {
    // Abrir modal inmediatamente y configurar estados
    setProductoSeleccionado(nombreProducto);
    setProductoIdSeleccionado(idProducto || null);
    setProductoCostoUnitSeleccionado(costoUnit ?? null);
    setModalProductoOpen(true);
    setLoadingModal(true);
    setPedidosProducto([]); // Limpiar datos anteriores

    try {
      const url = `reportes/detalle-pedidos-producto?fechaInicio=${dayjs(fechaInicio).format(
        "YYYY-MM-DD"
      )}&fechaFin=${dayjs(fechaFin).format("YYYY-MM-DD")}&idProducto=${idProducto}&categoria=${categoriaSeleccionada}${costoUnit !== null && costoUnit !== undefined ? `&costoUnit=${encodeURIComponent(costoUnit)}` : ""}${medioPagoSeleccionado !== "todos" ? `&medioPago=${encodeURIComponent(medioPagoSeleccionado)}` : ""}`;

      const response = await restLink.get(url);
      setPedidosProducto(response.data.pedidos || []);
    } catch (error) {
      console.error("Error al cargar pedidos por producto:", error);
      setPedidosProducto([]);
    } finally {
      setLoadingModal(false);
    }
  };

  // ----------------- Refrescar datos visibles -----------------
  const handleRefresh = async () => {
    setExpandedDays({})
    setRefreshing(true);
    try {
      // Refresca la vista principal
      await fetchReporteData();
      // Si hay modales abiertos, refrescarlos con los parámetros actuales
      if (modalSegmentoOpen && segmentoSeleccionado) {
        await fetchPedidosSegmento(segmentoSeleccionado, segmentoProductoId, productoNombreSeleccionado || null, segmentoCostoUnitSeleccionado);
      }
      if (modalProductoOpen && productoIdSeleccionado) {
        await fetchPedidosProducto(productoIdSeleccionado, productoSeleccionado || "", productoCostoUnitSeleccionado);
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleCategoriaSeleccion = (categoria) => {
    setCategoriaSeleccionada(categoria);
    setProductoFiltroSeleccionado(null);
    setProductoFiltroAplicado(null);
  };

  // Animación de giro para el ícono de refresh
  const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  `;

  // ----------------- Componente Pill -----------------
  const Pill = ({ label = "", tone = "default", onClick = null, clickable = false }) => {
    const styles = {
      default: { bg: "#eceff1", fg: "#37474f" },
      primary: { bg: "#e3f2fd", fg: "#1976d2" },
      secondary: { bg: "#f3e5f5", fg: "#7b1fa2" },
      warning: { bg: "#fff8e1", fg: "#e65100" },
    };
    const c = styles[tone] || styles.default;
    return (
      <SoftBox
        px={1}
        py={0.4}
        onClick={clickable && onClick ? onClick : undefined}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          borderRadius: "999px",
          background: c.bg,
          maxWidth: 360,
          cursor: clickable ? "pointer" : "default",
          "&:hover": clickable ? {
            background: c.bg,
            opacity: 0.8,
            transform: "scale(1.02)"
          } : {}
        }}
      >
        <SoftTypography
          variant="caption"
          sx={{
            color: c.fg,
            fontWeight: 700,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </SoftTypography>
      </SoftBox>
    );
  };

  // ----------------- Reporte Detallado por Día -----------------
  const Reporte = () => {
    const resumen = Array.isArray(reporteData?.resumen) ? reporteData.resumen : [];
    if (resumen.length === 0) {
      return (
        <Card sx={{ width: "100%", overflow: "hidden" }}>
          <SoftBox p={2}>
            <SoftTypography variant="button" color="text">
              No hay datos para el período seleccionado.
            </SoftTypography>
          </SoftBox>
        </Card>
      );
    }

    return (
      <Card sx={{ width: "100%", overflow: "hidden" }}>
        {resumen.map((dia, idx) => {
          const productos = Array.isArray(dia.productos) ? dia.productos : [];
          const totalDia = dia.totalDia || {};
          const dayKey = dayjs(dia.fecha).isValid() ? dayjs(dia.fecha).format("YYYY-MM-DD") : String(dia.fecha);
          const isOpen = !!expandedDays[dayKey];

          // Determinar si todos los productos del dia estan abiertos
          const productKeys = productos.map((p, i) => `${p.idProducto ?? `idx-${i}`}__${num(p?.subtotal?.costoUnit)}`);
          const allOpen = productKeys.length > 0 && productKeys.every((pk) => expandedProducts[dayKey]?.[pk]);

          return (
            <SoftBox key={`dia-${idx}`} sx={{ mb: 3, borderRadius: 2, boxShadow: 2, border: '1px solid #e0e0e0' }}>
              {/* Cabecera del dia con toggle */}
              <SoftBox
                onClick={() => setExpandedDays((prev) => ({ ...prev, [dayKey]: !isOpen }))}
                px={2}
                py={1.2}
                sx={{
                  background: isOpen ? "#e8f5e9" : "#f5f5f5",
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer'
                }}
              >
                <SoftBox display="flex" alignItems="center" gap={2}>
                  <Icon sx={{ transition: 'transform .2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>expand_more</Icon>
                  <SoftBox>
                    <SoftTypography variant="overline" color="text">
                      FECHA
                    </SoftTypography>
                    <SoftTypography variant="h6">
                      {dayjs(dia.fecha).isValid() ? dayjs(dia.fecha).format("DD/MM/YYYY") : str(dia.fecha)}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
                <SoftBox display="flex" alignItems="center" gap={2}>
                  <Pill tone="primary" label={`Unidades: ${num(totalDia.unidades)}`} />
                  <Pill tone="secondary" label={`Ventas: $${formatMoneyPunto(num(totalDia.ventaTotal))}`} />
                  <Pill tone="default" label={`Margen: ${formatPercent(totalDia.marg)}`} />
                  <Pill tone={num(totalDia.rentabilidad) >= 0 ? 'warning' : 'default'} label={`Rentab.: $${formatMoneyPunto(num(totalDia.rentabilidad))}`} />
                  {categoriaSeleccionada !== "cafeteria" &&
                    (<Tooltip title={allOpen ? 'Cerrar todas' : 'Expandir todas'}>
                      <SoftButton
                        size="small"
                        color={allOpen ? 'secondary' : 'primary'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedProducts((prev) => {
                            const map = {};
                            productKeys.forEach((pk) => { map[pk] = !allOpen; });
                            return { ...prev, [dayKey]: map };
                          });
                        }}
                      >
                        <Icon>{allOpen ? 'unfold_less' : 'unfold_more'}</Icon>
                      </SoftButton>
                    </Tooltip>)
                  }
                </SoftBox>
              </SoftBox>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>

                {/* Encabezados */}
                <SoftBox
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1.1fr 0.9fr 1.1fr 1.1fr 1.1fr 0.9fr 1.2fr",
                    backgroundColor: "#fafafa",
                    borderTop: "1px solid #e0e0e0",
                    minHeight: "44px",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <CellH sx={{ justifyContent: "flex-start", pl: 2 }}>PRODUCTO</CellH>
                  <CellH>Precio Unitario Vta</CellH>
                  <CellH>Unidades</CellH>
                  <CellH>Venta Total</CellH>
                  <CellH>Costo Unitario</CellH>
                  <CellH>Costo Total</CellH>
                  <CellH>Margen</CellH>
                  <CellH>Rentabilidad</CellH>
                </SoftBox>

                {/* Cuerpo */}
                <SoftBox sx={{ borderTop: "1px solid #e0e0e0" }}>
                  {productos.length === 0 && (
                    <SoftBox p={2}>
                      <SoftTypography variant="button" color="text">
                        Sin movimientos para esta fecha.
                      </SoftTypography>
                    </SoftBox>
                  )}

                  {productos.map((p, ip) => {
                    const lineas = Array.isArray(p.lineas) ? p.lineas : [];
                    const subtotal = p.subtotal || {};

                    const nombre = str(p.producto).toLowerCase();
                    const esVeg = nombre.includes("vegetariano");
                    const esGlutenFree = nombre.includes("libre de gluten");

                    const bgProducto = esVeg
                      ? "#e8f5e9"
                      : esGlutenFree
                        ? "#e3f2fd"
                        : "#ffe0b2";
                    const productKey = `${p.idProducto ?? `idx-${ip}`}__${num(p?.subtotal?.costoUnit)}`;
                    const isProdOpen = !!(expandedProducts[dayKey]?.[productKey]);
                    const costoProductoRaw = Number(p?.subtotal?.costoUnit);
                    const costoProductoRef = Number.isFinite(costoProductoRaw) ? costoProductoRaw : null;
                    const handleProductClick = () => {
                      if (!isProdOpen && categoriaSeleccionada !== "cafeteria") {
                        setExpandedProducts((prev) => ({
                          ...prev,
                          [dayKey]: { ...(prev[dayKey] || {}), [productKey]: true }
                        }));
                      } else {
                        fetchPedidosProducto(p.idProducto, str(p.producto), costoProductoRef);
                      }
                    };

                    return (
                      <SoftBox key={`prod-${ip}`} sx={{ borderBottom: "1px dashed #e0e0e0" }}>
                        {/* Producto */}
                        <RowC sx={{ background: bgProducto }}>
                          <Cell sx={{ justifyContent: "flex-start", pl: 2 }}>
                            <SoftTypography
                              variant="button"
                              sx={{
                                fontWeight: 700,
                                cursor: "pointer",
                                padding: "8px 12px",
                                borderRadius: "6px",
                                transition: "all 0.3s ease",
                                display: "inline-block",
                                "&:hover": {
                                  backgroundColor: "rgba(0, 0, 0, 0.08)",
                                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                  transform: "translateY(-1px)",
                                  color: getUproColor("uproBlue")
                                }
                              }}
                              onClick={handleProductClick}
                            >
                              {str(p.producto) || "-"}
                            </SoftTypography>
                          </Cell>
                          {isProdOpen ? (
                            <>
                              <Cell /><Cell /><Cell /><Cell /><Cell /><Cell /><Cell />
                            </>
                          ) : (
                            <>
                              <Cell />
                              <Cell center>
                                <SoftTypography variant="button">{num(subtotal.unidades)}</SoftTypography>
                              </Cell>
                              <Cell center>
                                <SoftTypography variant="button">${formatMoneyPunto(num(subtotal.ventaTotal))}</SoftTypography>
                              </Cell>
                              <Cell center>
                                <SoftTypography variant="button">${formatMoneyPunto(num(subtotal.costoUnit))}</SoftTypography>
                              </Cell>
                              <Cell center>
                                <SoftTypography variant="button">${formatMoneyPunto(num(subtotal.costoTotal))}</SoftTypography>
                              </Cell>
                              <Cell center>
                                <SoftTypography variant="button">{formatPercent(subtotal.marg)}</SoftTypography>
                              </Cell>
                              <Cell center>
                                <SoftTypography
                                  variant="button"
                                  sx={{ color: num(subtotal.rentabilidad) >= 0 ? "green" : "red", fontWeight: 800 }}
                                >
                                  ${formatMoneyPunto(num(subtotal.rentabilidad))}
                                </SoftTypography>
                              </Cell>
                            </>
                          )}
                        </RowC>

                        {/* Líneas */}
                        <Collapse in={isProdOpen} timeout="auto" unmountOnExit>
                          {lineas.length === 0 ? (
                            <SoftBox p={2}>
                              <SoftTypography variant="caption" color="text">
                                Sin líneas para este producto.
                              </SoftTypography>
                            </SoftBox>
                          ) : (
                            lineas.map((l, il) => (
                              <RowC
                                key={`linea-${ip}-${il}`}
                                sx={{
                                  background: '#fff',
                                  border: '1px solid #eee',
                                  borderRadius: '8px',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                  my: 0.5
                                }}
                              >
                                <Cell sx={{ justifyContent: "flex-start", pl: 2 }}>
                                  <Pill
                                    label={str(l.segmento) || "-"}
                                    clickable={true}
                                    onClick={() => fetchPedidosSegmento(str(l.segmento), p.idProducto, str(p.producto), num(l.costoUnit))}
                                    tone={
                                      str(l.segmento).startsWith("Estudiante UPRO")
                                        ? "primary"
                                        : str(l.segmento).startsWith("Estudiante Convenio")
                                          ? "secondary"
                                          : str(l.segmento).startsWith("Personal UPRO")
                                            ? "warning"
                                            : str(l.segmento).startsWith("Personal Convenio")
                                              ? "warning"
                                              : "default"
                                    }
                                  />
                                </Cell>
                                <Cell center>
                                  <SoftTypography variant="button">${formatMoneyPunto(num(l.precioUnit))}</SoftTypography>
                                </Cell>
                                <Cell center>
                                  <SoftTypography variant="button">{num(l.unidades)}</SoftTypography>
                                </Cell>
                                <Cell center>
                                  <SoftTypography variant="button">${formatMoneyPunto(num(l.ventaTotal))}</SoftTypography>
                                </Cell>
                                <Cell center>
                                  <SoftTypography variant="button">${formatMoneyPunto(num(l.costoUnit))}</SoftTypography>
                                </Cell>
                                <Cell center>
                                  <SoftTypography variant="button">${formatMoneyPunto(num(l.costoTotal))}</SoftTypography>
                                </Cell>
                                <Cell center>
                                  <SoftTypography variant="button" sx={{ fontWeight: 800 }}>
                                    {formatPercent(l.marg)} {/* ✅ Usamos formatPercent */}
                                  </SoftTypography>
                                </Cell>
                                <Cell center>
                                  <SoftTypography
                                    variant="button"
                                    sx={{ fontWeight: 900, color: num(l.rentabilidad) >= 0 ? "green" : "red" }}
                                  >
                                    ${formatMoneyPunto(num(l.rentabilidad))} {/* ✅ Con signo */}
                                  </SoftTypography>
                                </Cell>
                              </RowC>
                            ))
                          )}

                          {/* Subtotal */}
                          <RowC sx={{ background: "#fff59d", borderTop: "1px solid #f0e0c2" }}>
                            <Cell sx={{ justifyContent: "flex-start", pl: 2 }}>
                              <SoftTypography variant="button" sx={{ fontWeight: 700 }}>
                                Subtotal
                              </SoftTypography>
                            </Cell>
                            <Cell />
                            <Cell center>
                              <SoftTypography variant="button">{num(subtotal.unidades)}</SoftTypography>
                            </Cell>
                            <Cell center>
                              <SoftTypography variant="button">${formatMoneyPunto(num(subtotal.ventaTotal))}</SoftTypography>
                            </Cell>
                            <Cell center>
                              <SoftTypography variant="button">${formatMoneyPunto(num(subtotal.costoUnit))}</SoftTypography>
                            </Cell>
                            <Cell center>
                              <SoftTypography variant="button">${formatMoneyPunto(num(subtotal.costoTotal))}</SoftTypography>
                            </Cell>
                            <Cell center>
                              <SoftTypography variant="button">{formatPercent(subtotal.marg)}</SoftTypography>
                            </Cell>
                            <Cell center>
                              <SoftTypography
                                variant="button"
                                sx={{ color: num(subtotal.rentabilidad) >= 0 ? "green" : "red", fontWeight: 800 }}
                              >
                                ${formatMoneyPunto(num(subtotal.rentabilidad))}
                              </SoftTypography>
                            </Cell>
                          </RowC>
                        </Collapse>
                      </SoftBox>
                    );
                  })}

                  {/* TOTAL del día */}
                  <RowC sx={{ background: "#eefaf0", borderTop: "2px solid #cfe7d5" }}>
                    <Cell sx={{ justifyContent: "flex-start", pl: 2 }}>
                      <SoftTypography variant="button" sx={{ fontWeight: 900 }}>
                        TOTAL
                      </SoftTypography>
                    </Cell>
                    <Cell />
                    <Cell center>
                      <SoftTypography variant="button">{num(totalDia.unidades)}</SoftTypography>
                    </Cell>
                    <Cell center>
                      <SoftTypography variant="button">${formatMoneyPunto(num(totalDia.ventaTotal))}</SoftTypography>
                    </Cell>
                    <Cell />
                    <Cell center>
                      <SoftTypography variant="button">${formatMoneyPunto(num(totalDia.costoTotal))}</SoftTypography>
                    </Cell>
                    <Cell center>
                      <SoftTypography variant="button">{formatPercent(totalDia.marg)}</SoftTypography>
                    </Cell>
                    <Cell center>
                      <SoftTypography
                        variant="button"
                        sx={{ color: num(totalDia.rentabilidad) >= 0 ? "green" : "red", fontWeight: 900 }}
                      >
                        ${formatMoneyPunto(num(totalDia.rentabilidad))}
                      </SoftTypography>
                    </Cell>
                  </RowC>
                </SoftBox>
              </Collapse>
            </SoftBox>
          );
        })}
      </Card>
    );
  };

  // ----------------- Resumen Ejecutivo -----------------
  const ResumenEjecutivo = () => {
    // Calcular totales desde líneas individuales para consistencia con Excel
    const totalesFromLineas = (() => {
      const r = reporteData?.resumen;
      if (!Array.isArray(r) || r.length === 0) return null;

      return r.reduce(
        (acc, d) => {
          (d.productos || []).forEach((p) => {
            (p.lineas || []).forEach((l) => {
              acc.unidadesVendidas += Number(l.unidades) || 0;
              acc.ventas += Number(l.ventaTotal) || 0;
              acc.costos += Number(l.costoTotal) || 0;
              acc.rentabilidad += Number(l.rentabilidad) || 0;
            });
          });
          return acc;
        },
        { unidadesVendidas: 0, ventas: 0, costos: 0, rentabilidad: 0 }
      );
    })();

    const totales = totalesFromLineas
      ? {
        unidadesVendidas: totalesFromLineas.unidadesVendidas,
        ventas: totalesFromLineas.ventas,
        costos: totalesFromLineas.costos,
        // Mostrar margen ponderado calculado desde líneas individuales
        margen: totalesFromLineas.ventas > 0
          ? (totalesFromLineas.ventas - totalesFromLineas.costos) / totalesFromLineas.ventas
          : 0,
        rentabilidad: totalesFromLineas.rentabilidad,
      }
      : null;

    if (!totales) return null;

    return (
      <Card sx={{ mb: 2 }}>
        <SoftBox p={3}>
          <SoftTypography variant="h6" mb={2}>
            Resumen Ejecutivo
          </SoftTypography>
          <SoftBox display="flex" justifyContent="space-around" flexWrap="wrap" gap={2}>
            <KPI label="Unidades Vendidas" value={totales.unidadesVendidas} color="success" money={false} />
            <KPI label="Ventas Totales" value={totales.ventas} color="info" />
            <KPI label="Costos Totales" value={totales.costos} color="warning" />
            <SoftBox textAlign="center">
              <SoftTypography
                variant="h4"
                color={totales.margen > 0.4 ? "success" : totales.margen > 0.2 ? "warning" : "error"}
              >
                {formatPercent(totales.margen)} {/* ✅ Unificado */}
              </SoftTypography>
              <SoftTypography variant="caption" color="text">
                Margen Promedio
              </SoftTypography>
            </SoftBox>
            <SoftBox textAlign="center">
              <SoftTypography
                variant="h4"
                sx={{ color: totales.rentabilidad >= 0 ? "green" : "red" }}
              >
                ${formatMoneyPunto(totales.rentabilidad)}
              </SoftTypography>
              <SoftTypography variant="caption" color="text">
                Rentabilidad Total
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </SoftBox>
      </Card>
    );
  };

  const KPI = ({ label, value, color = "info", money = true }) => (
    <SoftBox textAlign="center">
      <SoftTypography variant="h4" color={color}>
        {money ? `$${formatMoneyPunto(value || 0)}` : value || 0}
      </SoftTypography>
      <SoftTypography variant="caption" color="text">
        {label}
      </SoftTypography>
    </SoftBox>
  );

  // ----------------- Tabla Resumen Acumulado -----------------
  const TablaResumenAcumulado = () => {
    const resumen = Array.isArray(reporteData?.resumen) ? reporteData.resumen : [];
    if (resumen.length === 0) {
      return (
        <Card>
          <SoftBox p={2}>
            <SoftTypography variant="button" color="text">
              Sin datos
            </SoftTypography>
          </SoftBox>
        </Card>
      );
    }

    const porCliente = new Map();
    resumen.forEach((d) => {
      (d.productos || []).forEach((p) => {
        (p.lineas || []).forEach((l) => {
          const seg = str(l.segmento);
          const agg = porCliente.get(seg) || { u: 0, v: 0, c: 0, r: 0 };
          agg.u += num(l.unidades);
          agg.v += num(l.ventaTotal);
          agg.c += num(l.costoTotal);
          agg.r += num(l.rentabilidad);
          porCliente.set(seg, agg);
        });
      });
    });

    const orden = (t) => {
      const s = t.toLowerCase();
      if (s.startsWith("estudiante upro")) return 1;
      if (s.startsWith("estudiante convenio")) return 2;
      if (s.startsWith("personal upro")) return 3;
      if (s.startsWith("personal convenio")) return 4;
      if (s.startsWith("particular")) return 5;
      return 6;
    };

    const filas = Array.from(porCliente.entries())
      .map(([cliente, v]) => {
        const margenRatio = v.v > 0 ? (v.v - v.c) / v.v : 0;
        return {
          cliente,
          unidades: v.u,
          ventas: v.v,
          costos: v.c,
          margenRatio,
          rentabilidad: v.r,
        };
      })
      .sort((a, b) => {
        const oa = orden(a.cliente);
        const ob = orden(b.cliente);
        return oa === ob ? a.cliente.localeCompare(b.cliente) : oa - ob;
      });

    const tot = filas.reduce(
      (acc, f) => {
        acc.u += f.unidades;
        acc.v += f.ventas;
        acc.c += f.costos;
        acc.r += f.rentabilidad;
        return acc;
      },
      { u: 0, v: 0, c: 0, r: 0 }
    );
    const margenTotRatio = tot.v > 0 ? (tot.v - tot.c) / tot.v : 0;

    const periodo = `${dayjs(fechaInicio).format("DD/MM/YY")} al ${dayjs(fechaFin).format("DD/MM/YY")}`;

    return (
      <>
        <Card sx={{ width: "100%" }}>
          <SoftBox p={2} sx={{ textAlign: "center", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <SoftTypography variant="h6">ACUMULADO</SoftTypography>
            <Tooltip title="Exportar PDF" placement="top">
              <SoftButton color="error" onClick={exportarPDF} circular disabled={loading || !reporteData}>
                <Icon>picture_as_pdf</Icon>&nbsp;PDF
              </SoftButton>
            </Tooltip>
          </SoftBox>

          <SoftBox
            sx={{
              display: "grid",
              gridTemplateColumns: "1.2fr 2fr 1fr 1.2fr 1.2fr 0.8fr 1.4fr",
              background: "#fff59d",
              borderTop: "1px solid #e8e8e8",
              borderBottom: "2px solid #e8e8e8",
              minHeight: 44,
              alignItems: "center",
            }}
          >
            <CellH>Periodo</CellH>
            <CellH>CLIENTE</CellH>
            <CellH>Unidades Vendidas</CellH>
            <CellH>Ventas</CellH>
            <CellH>Costos</CellH>
            <CellH>Margen</CellH>
            <CellH>Rentabilidad</CellH>
          </SoftBox>

          {filas.map((f, i) => (
            <SoftBox
              key={i}
              sx={{
                display: "grid",
                gridTemplateColumns: "1.2fr 2fr 1fr 1.2fr 1.2fr 0.8fr 1.4fr",
                borderBottom: "1px solid #eee",
                minHeight: 42,
                alignItems: "center",
              }}
            >
              <Cell center>
                <SoftTypography variant="button">{i === 0 ? periodo : ""}</SoftTypography>
              </Cell>
              <Cell sx={{ justifyContent: "flex-start" }}>
                <SoftTypography variant="button">{f.cliente}</SoftTypography>
              </Cell>
              <Cell center>
                <SoftTypography variant="button">{f.unidades}</SoftTypography>
              </Cell>
              <Cell center>
                <SoftTypography variant="button">${formatMoneyPunto(f.ventas)}</SoftTypography>
              </Cell>
              <Cell center>
                <SoftTypography variant="button">${formatMoneyPunto(f.costos)}</SoftTypography>
              </Cell>
              <Cell center>
                <SoftTypography variant="button">{formatPercent(f.margenRatio)}</SoftTypography>
              </Cell>
              <Cell center>
                <SoftTypography variant="button">${formatMoneyPunto(f.rentabilidad)}</SoftTypography>
              </Cell>
            </SoftBox>
          ))}

          <SoftBox
            sx={{
              display: "grid",
              gridTemplateColumns: "1.2fr 2fr 1fr 1.2fr 1.2fr 0.8fr 1.4fr",
              borderTop: "2px solid #e8e8e8",
              background: "#f7f7f7",
              minHeight: 44,
              alignItems: "center",
            }}
          >
            <Cell />
            <Cell sx={{ justifyContent: "flex-start" }}>
              <SoftTypography variant="button" sx={{ fontWeight: 800 }}>
                TOTAL
              </SoftTypography>
            </Cell>
            <Cell center>
              <SoftTypography variant="button" sx={{ fontWeight: 800 }}>
                {tot.u}
              </SoftTypography>
            </Cell>
            <Cell center>
              <SoftTypography variant="button" sx={{ fontWeight: 800 }}>
                ${formatMoneyPunto(tot.v)}
              </SoftTypography>
            </Cell>
            <Cell center>
              <SoftTypography variant="button" sx={{ fontWeight: 800 }}>
                ${formatMoneyPunto(tot.c)}
              </SoftTypography>
            </Cell>
            <Cell center>
              <SoftTypography variant="button" sx={{ fontWeight: 800 }}>
                {formatPercent(margenTotRatio)}
              </SoftTypography>
            </Cell>
            <Cell center>
              <SoftTypography variant="button" sx={{ fontWeight: 800 }}>
                ${formatMoneyPunto(tot.r)}
              </SoftTypography>
            </Cell>
          </SoftBox>
        </Card>

        {/* Contenido oculto para PDF */}
        <SoftBox
          ref={pdfRef}
          sx={{
            display: 'none',
            backgroundColor: 'white',
            p: 3,
            minHeight: '297mm',
            width: '210mm',
            fontSize: '12px'
          }}
        >
          <PdfHeader />

          <SoftBox display="flex" flexDirection="column" gap={2}>
            <Grid container border="1pt solid black" lineHeight={1}>
              <Grid item px={6} py={1} xs={12} borderBottom="1pt solid black" textAlign="center">
                <SoftTypography variant="caption" fontWeight="bold">
                  <EditableText
                    tooltip="TÍTULO DEL REPORTE"
                    editable={false}
                    content={textArray[16]}
                    handleOnBlur={(e) => handleTextChange(e, 16)}
                  /> - {periodo}
                </SoftTypography>
              </Grid>
            </Grid>

            <Grid
              container
              border="1pt solid black"
              sx={{
                '& > *:not(:last-child)': { borderBottom: "1pt solid black" },
                '& > *:nth-child(-n+2)': { backgroundColor: "rgba(230, 230, 230)" }
              }}
              lineHeight={1}
            >
              <Grid item xs={12} p={1}>
                <SoftTypography variant="caption" fontWeight="bold">DATOS ACUMULADOS</SoftTypography>
              </Grid>

              <Grid item xs={12} container sx={{ '& > *:not(:last-child)': { borderRight: "1pt solid black" } }}>
                <Grid item xs={2} p={1}>
                  <SoftTypography variant="caption">CLIENTE</SoftTypography>
                </Grid>
                <Grid item xs={2} p={1}>
                  <SoftTypography variant="caption">Unidades Vendidas</SoftTypography>
                </Grid>
                <Grid item xs={2} p={1}>
                  <SoftTypography variant="caption">Ventas</SoftTypography>
                </Grid>
                <Grid item xs={2} p={1}>
                  <SoftTypography variant="caption">Costos</SoftTypography>
                </Grid>
                <Grid item xs={2} p={1}>
                  <SoftTypography variant="caption">Margen</SoftTypography>
                </Grid>
                <Grid item xs={2} p={1}>
                  <SoftTypography variant="caption">Rentabilidad</SoftTypography>
                </Grid>
              </Grid>

              {filas.map((fila, index) => (
                <Grid key={index} item xs={12} container sx={{ '& > *:not(:last-child)': { borderRight: "1pt solid black" } }}>
                  <Grid item xs={2} p={1}>
                    <SoftTypography variant="caption">{fila.cliente}</SoftTypography>
                  </Grid>
                  <Grid item xs={2} p={1}>
                    <SoftTypography variant="caption">{fila.unidades}</SoftTypography>
                  </Grid>
                  <Grid item xs={2} p={1}>
                    <SoftTypography variant="caption">$ {formatMoneyPunto(fila.ventas)}</SoftTypography>
                  </Grid>
                  <Grid item xs={2} p={1}>
                    <SoftTypography variant="caption">$ {formatMoneyPunto(fila.costos)}</SoftTypography>
                  </Grid>
                  <Grid item xs={2} p={1}>
                    <SoftTypography variant="caption">{formatPercent(fila.margenRatio)}</SoftTypography>
                  </Grid>
                  <Grid item xs={2} p={1}>
                    <SoftTypography variant="caption">$ {formatMoneyPunto(fila.rentabilidad)}</SoftTypography>
                  </Grid>
                </Grid>
              ))}
            </Grid>

            <Grid
              container
              border="1pt solid black"
              sx={{
                '& > *:not(:last-child)': { borderBottom: "1pt solid black" },
                '& > *:nth-child(-n+5)': { backgroundColor: "rgba(230, 230, 230)" }
              }}
              lineHeight={1}
            >
              <Grid item xs={12} px={1} py={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  TOTAL: {categoriasDisplay[categoriaSeleccionada] || categoriaSeleccionada}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} px={1} py={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Total Unidades Vendidas: {tot.u}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} px={1} py={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Total Ventas: $ {formatMoneyPunto(tot.v)}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} px={1} py={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Total Costos: $ {formatMoneyPunto(tot.c)}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} px={1} py={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Margen Promedio: {formatPercent(margenTotRatio)}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} px={1} py={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Rentabilidad Total: $ {formatMoneyPunto(tot.r)}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} px={1} py={0.5}>
                <SoftTypography variant="caption">
                  Período analizado: {periodo} - Categoría: {categoriasDisplay[categoriaSeleccionada] || categoriaSeleccionada}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} px={1} py={0.5}>
                <SoftTypography variant="caption">
                  Reporte generado el: {dayjs().format("DD/MM/YYYY HH:mm")}
                </SoftTypography>
              </Grid>
            </Grid>
          </SoftBox>
        </SoftBox>
      </>
    );
  };

  // ----------------- Exportar a Excel -----------------
  const exportarExcel = () => {
    if (!reporteData) return;

    // Helper para convertir valores a números manteniendo el formato correcto
    const toNumber = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    };

    // Helper para convertir porcentajes a números decimales
    const percentToDecimal = (value) => {
      const n = Number(value);
      if (!Number.isFinite(n)) return 0;
      // Si el valor está entre -1.5 y 1.5, asumimos que ya es decimal
      return Math.abs(n) <= 1.5 ? n : n / 100;
    };

    const wb = XLSX.utils.book_new();
    const wsData = [];

    // Acumuladores para el Total General (suma de líneas individuales, no subtotales)
    let totalUnidades = 0;
    let totalVenta = 0;
    let totalCosto = 0;
    let totalRentabilidad = 0;

    reporteData.resumen.forEach((dia) => {
      const fecha = dayjs(dia.fecha).format("DD/MM/YYYY");
      (dia.productos || []).forEach((p) => {
        (p.lineas || []).forEach((l) => {
          wsData.push({
            Fecha: fecha,
            Producto: p.producto,
            Cliente: l.segmento,
            "Precio Unitario Vta": toNumber(l.precioUnit),
            "Unidades Vendidas": toNumber(l.unidades),
            "Venta Total": toNumber(l.ventaTotal),
            "Costo Unitario": toNumber(l.costoUnit),
            "Costo Total": toNumber(l.costoTotal),
            Margen: percentToDecimal(l.marg),
            Rentabilidad: toNumber(l.rentabilidad),
          });

          // Acumular desde las líneas individuales para evitar discrepancias
          totalUnidades += toNumber(l.unidades);
          totalVenta += toNumber(l.ventaTotal);
          totalCosto += toNumber(l.costoTotal);
          totalRentabilidad += toNumber(l.rentabilidad);
        });
        // Se eliminó la fila SUBTOTAL por producto
      });
      wsData.push({
        Fecha: `${fecha} - TOTAL`,
        Producto: "",
        Cliente: "",
        "Precio Unitario Vta": "",
        "Unidades Vendidas": toNumber(dia.totalDia?.unidades),
        "Venta Total": toNumber(dia.totalDia?.ventaTotal),
        "Costo Unitario": "",
        "Costo Total": toNumber(dia.totalDia?.costoTotal),
        Margen: percentToDecimal(dia.totalDia?.marg || 0),
        Rentabilidad: toNumber(dia.totalDia?.rentabilidad),
      });
    });

    // Agregar fila de Total General (suma de todos los subtotales)
    wsData.push({
      Fecha: `TOTAL GENERAL`,
      Producto: "",
      Cliente: "",
      "Precio Unitario Vta": "",
      "Unidades Vendidas": toNumber(totalUnidades),
      "Venta Total": toNumber(totalVenta),
      "Costo Unitario": "",
      "Costo Total": toNumber(totalCosto),
      // Margen ponderado: (Venta - Costo) / Venta
      Margen: percentToDecimal(totalVenta !== 0 ? (totalVenta - totalCosto) / totalVenta : 0),
      Rentabilidad: toNumber(totalRentabilidad),
    });

    const ws1 = XLSX.utils.json_to_sheet(wsData);

    // Aplicar formato de porcentaje a la columna Margen y moneda a totales
    const range = XLSX.utils.decode_range(ws1['!ref']);
    for (let row = 1; row <= range.e.r; row++) {
      const margenCell = XLSX.utils.encode_cell({ r: row, c: 8 }); // Columna I (Margen)
      if (ws1[margenCell] && typeof ws1[margenCell].v === 'number') {
        ws1[margenCell].z = '0.00%';
      }
      const ventaCell = XLSX.utils.encode_cell({ r: row, c: 5 }); // Columna F (Venta Total)
      if (ws1[ventaCell] && typeof ws1[ventaCell].v === 'number') {
        ws1[ventaCell].z = '#,##0.00';
      }
      const costoCell = XLSX.utils.encode_cell({ r: row, c: 7 }); // Columna H (Costo Total)
      if (ws1[costoCell] && typeof ws1[costoCell].v === 'number') {
        ws1[costoCell].z = '#,##0.00';
      }
      const rentCell = XLSX.utils.encode_cell({ r: row, c: 9 }); // Columna J (Rentabilidad)
      if (ws1[rentCell] && typeof ws1[rentCell].v === 'number') {
        ws1[rentCell].z = '#,##0.00';
      }
    }

    // Agregar hoja de RESUMEN calculada desde líneas individuales (para consistencia)
    const resumenData = [{
      'Unidades Vendidas': toNumber(totalUnidades),
      'Venta Total': toNumber(totalVenta),
      'Costo Total': toNumber(totalCosto),
      'Margen': totalVenta !== 0 ? (totalVenta - totalCosto) / totalVenta : 0,
      'Rentabilidad': toNumber(totalRentabilidad),
    }];
    const wsResumen = XLSX.utils.json_to_sheet(resumenData);
    const rangeR = XLSX.utils.decode_range(wsResumen['!ref']);
    for (let row = 1; row <= rangeR.e.r; row++) {
      const margenR = XLSX.utils.encode_cell({ r: row, c: 3 }); // Columna D (Margen)
      if (wsResumen[margenR] && typeof wsResumen[margenR].v === 'number') {
        wsResumen[margenR].z = '0.00%';
      }
      const ventaR = XLSX.utils.encode_cell({ r: row, c: 1 }); // Columna B (Venta Total)
      if (wsResumen[ventaR] && typeof wsResumen[ventaR].v === 'number') {
        wsResumen[ventaR].z = '#,##0.00';
      }
      const costoR = XLSX.utils.encode_cell({ r: row, c: 2 }); // Columna C (Costo Total)
      if (wsResumen[costoR] && typeof wsResumen[costoR].v === 'number') {
        wsResumen[costoR].z = '#,##0.00';
      }
      const rentR = XLSX.utils.encode_cell({ r: row, c: 4 }); // Columna E (Rentabilidad)
      if (wsResumen[rentR] && typeof wsResumen[rentR].v === 'number') {
        wsResumen[rentR].z = '#,##0.00';
      }
    }

    // Hacer que RESUMEN sea la primera hoja para mayor claridad
    XLSX.utils.book_append_sheet(wb, wsResumen, "RESUMEN");
    XLSX.utils.book_append_sheet(wb, ws1, "COMEDOR ING-COST");

    // Agregar hoja con Top 10 Productos
    if (reporteData.top10Productos && reporteData.top10Productos.length > 0) {
      const top10Data = reporteData.top10Productos.map((prod) => ({
        Posicion: toNumber(prod.posicion),
        Producto: prod.producto,
        "Unidades Vendidas": toNumber(prod.unidadesTotales),
        "Venta Total": toNumber(prod.ventaTotal),
        "Costo Total": toNumber(prod.costoTotal),
        "Margen Promedio": percentToDecimal(prod.margenPromedio),
        "Rentabilidad Total": toNumber(prod.rentabilidadTotal),
      }));

      const ws2 = XLSX.utils.json_to_sheet(top10Data);

      // Aplicar formato de porcentaje a la columna Margen Promedio en Top 10
      const range2 = XLSX.utils.decode_range(ws2['!ref']);
      for (let row = 1; row <= range2.e.r; row++) {
        const margenCell = XLSX.utils.encode_cell({ r: row, c: 5 }); // Columna F (Margen Promedio)
        if (ws2[margenCell] && typeof ws2[margenCell].v === 'number') {
          ws2[margenCell].z = '0.00%';
        }
        const ventaCell2 = XLSX.utils.encode_cell({ r: row, c: 3 }); // Columna D (Venta Total)
        if (ws2[ventaCell2] && typeof ws2[ventaCell2].v === 'number') {
          ws2[ventaCell2].z = '#,##0.00';
        }
        const costoCell2 = XLSX.utils.encode_cell({ r: row, c: 4 }); // Columna E (Costo Total)
        if (ws2[costoCell2] && typeof ws2[costoCell2].v === 'number') {
          ws2[costoCell2].z = '#,##0.00';
        }
        const rentCell2 = XLSX.utils.encode_cell({ r: row, c: 6 }); // Columna G (Rentabilidad Total)
        if (ws2[rentCell2] && typeof ws2[rentCell2].v === 'number') {
          ws2[rentCell2].z = '#,##0.00';
        }
      }

      XLSX.utils.book_append_sheet(wb, ws2, "TOP 10 PRODUCTOS");
    }

    const fechaTexto =
      fechaOption === "hoy"
        ? "Hoy"
        : fechaOption === "ayer"
          ? "Ayer"
          : `${dayjs(fechaInicio).format("DD-MM-YYYY")}_${dayjs(fechaFin).format("DD-MM-YYYY")}`;

    const categoriaTexto = categoriasDisplay[categoriaSeleccionada] || categoriaSeleccionada;
    const categoriaTextoArchivo = categoriaTexto.replace(/\s+/g, "_");

    XLSX.writeFile(wb, `Analisis_Ingresos_Costos_${categoriaTextoArchivo}_${fechaTexto}.xlsx`);
  };

  // ----------------- Función para manejar cambios de texto editable -----------------
  const handleTextChange = (e, index) => {
    const newTextArray = [...textArray];
    newTextArray[index] = e.target.value;
    setTextArray(newTextArray);
  };

  // ----------------- Función para generar PDF -----------------
  const exportarPDF = async () => {
    if (!reporteData || !pdfRef.current) return;

    try {
      // Temporalmente hacer visible el contenido del PDF
      const element = pdfRef.current;
      const originalDisplay = element.style.display;
      element.style.display = 'block';

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);

      const fechaTexto =
        fechaOption === "hoy"
          ? "Hoy"
          : fechaOption === "ayer"
            ? "Ayer"
            : `${dayjs(fechaInicio).format("DD-MM-YYYY")}_${dayjs(fechaFin).format("DD-MM-YYYY")}`;

      const categoriaTexto = categoriasDisplay[categoriaSeleccionada] || categoriaSeleccionada;
      const categoriaTextoArchivo = categoriaTexto.replace(/\s+/g, "_");

      pdf.save(`Reporte_Acumulado_${categoriaTextoArchivo}_${fechaTexto}.pdf`);

      // Restaurar el display original
      element.style.display = originalDisplay;
    } catch (error) {
      console.error('Error al generar PDF:', error);
    }
  };

  // ----------------- Helpers visuales -----------------
  const CellH = ({ children, sx }) => (
    <SoftBox
      sx={{
        p: 1,
        textAlign: "center",
        borderRight: "1px solid #e0e0e0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...sx,
      }}
    >
      <SoftTypography variant="caption" sx={{ fontWeight: 800, color: "text.main" }}>
        {children}
      </SoftTypography>
    </SoftBox>
  );

  const Cell = ({ children, center, sx, title }) => (
    <SoftBox
      title={title}
      sx={{
        p: 1,
        textAlign: center ? "center" : "left",
        borderRight: "1px solid #eee",
        display: "flex",
        alignItems: "center",
        ...sx,
      }}
    >
      {typeof children === "string" || typeof children === "number" ? (
        <SoftTypography variant="button" sx={{ color: "text.main" }}>
          {children}
        </SoftTypography>
      ) : (
        children
      )}
    </SoftBox>
  );

  const RowC = ({ children, sx, ...rest }) => (
    <SoftBox
      sx={{
        display: "grid",
        gridTemplateColumns: "2fr 1.1fr 0.9fr 1.1fr 1.1fr 1.1fr 0.9fr 1.2fr",
        borderBottom: "1px solid #f0f0f0",
        minHeight: "44px",
        alignItems: "center",
        width: "100%",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </SoftBox>
  );

  // ----------------- Layout principal -----------------
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox display="flex" flexDirection="column" alignItems="center" gap={2}>
        <SoftBox width="100%">
          <Card>
            <SoftBox p={3} display="flex" flexDirection="column" gap={2}>
              <SoftBox p={3} display="flex" justifyContent="space-between" alignItems="center">
                <SoftTypography variant="h6">Análisis Ingresos - Costos</SoftTypography>
                <SoftBox display="flex" alignItems="center" gap={1}>
                  <SoftTypography variant="button" color="text" sx={{ opacity: 0.8 }}>
                    Periodo: {fechaResumen}
                  </SoftTypography>
                  <IconButton
                    onClick={handleRefresh}
                    disabled={loading || refreshing}
                    size="small"
                    sx={{ color: getUproColor("uproBlue") }}
                    aria-label="Actualizar datos"
                    title="Actualizar datos"
                  >
                    <Icon fontSize="small">refresh</Icon>
                  </IconButton>
                </SoftBox>
              </SoftBox>
              <SoftBox
                display="flex"
                justifyContent="flex-end"
                flexDirection={{ xs: "column", md: "row" }}
                gap={1.5}
                flexWrap="wrap"
              >
                {
                  (productoFiltroSeleccionado || productoFiltroAplicado) ? (
                    <SoftBox display="flex" alignItems="center" justifyContent="space-between">
                      <SoftTypography variant="h6" fontWeight="bold">
                        <SoftBox display="flex" alignItems="center">
                          <SoftTypography variant="h6" color="secondary">
                            &nbsp;&nbsp;{`${productoFiltroSeleccionado?.nombre}`}
                          </SoftTypography>
                        </SoftBox>
                      </SoftTypography>
                      <Tooltip placement="top" title="Cambiar producto">
                        <SoftButton
                          color="primary"
                          circular
                          iconOnly
                          onClick={handleLimpiarProducto}
                          sx={{ mx: 1 }}
                        >
                          <Icon sx={{ fontSize: "small" }}>close</Icon>
                        </SoftButton>
                      </Tooltip>
                    </SoftBox>) : (
                    <SoftBox display="flex" alignItems="center" justifyContent="space-between">
                      <Autocomplete
                        disableClearable={true}
                        options={productosOptions}
                        value={productoFiltroSeleccionado}
                        onChange={(event, newValue, reason) => {
                          if (reason === "clear" || !newValue) {
                            handleLimpiarProducto();
                          } else {
                            setProductoFiltroSeleccionado(newValue);
                            fetchReporteData(newValue || null);
                          }
                        }}
                        getOptionLabel={(option) => option?.nombre ?? ""}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        loading={productosLoading}
                        noOptionsText={productosLoading ? "Cargando..." : "Sin productos"}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Buscar producto"
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                            }}
                          />
                        )}
                      // clearOnEscape
                      />
                    </SoftBox>
                  )
                }
                {fechaOption === "personalizado" && (
                  <SoftBox
                    display="flex"
                    justifyContent="flex-end"
                    gap={1}
                    alignItems="center"
                  >
                    <SoftInput
                      type="date"
                      value={dayjs(fechaInicio).format("YYYY-MM-DD")}
                      onChange={(e) => {
                        setFechaInicio(dayjs(e.target.value).startOf("day").toDate());
                      }}
                      sx={{ width: { xs: "48%", sm: "160px" } }}
                    />
                    <SoftInput
                      type="date"
                      value={dayjs(fechaFin).format("YYYY-MM-DD")}
                      onChange={(e) => {
                        setFechaFin(dayjs(e.target.value).endOf("day").toDate());
                      }}
                      sx={{ width: { xs: "48%", sm: "160px" } }}
                    />
                  </SoftBox>
                )}
                <SoftBox display="flex" gap={1} alignItems="center" sx={{ flexShrink: 0 }}>
                  <Tooltip title="Buscar" placement="top">
                    <SoftButton color="primary" circular onClick={handleBuscar} disabled={loading}>
                      <Icon>search</Icon>
                    </SoftButton>
                  </Tooltip>
                </SoftBox>
                <SoftBox
                  display="flex"
                  gap={1.5}
                  flexWrap="wrap"
                  justifyContent={{ xs: "flex-start", md: "flex-end" }}
                >
                  <SoftButton
                    color="secondary"
                    circular
                    onClick={(event) => {
                      setExpandCategoriaActive(true);
                      menu({
                        open: event.currentTarget,
                        align: "right",
                        options: [
                          {
                            name: "Menu Universitario",
                            icon: { icon: "restaurant_menu", color: getUproColor(categoriaSeleccionada === "menuUniversitario" ? "uproGreen" : "uproBlue") },
                            onClick: () => {
                              handleCategoriaSeleccion("menuUniversitario");
                            },
                          },
                          {
                            name: "Cafetería",
                            icon: { icon: "local_cafe", color: getUproColor(categoriaSeleccionada === "cafeteria" ? "uproGreen" : "uproBlue") },
                            onClick: () => {
                              handleCategoriaSeleccion("cafeteria");
                            },
                          },
                          // {
                          //   name: "UPrODay",
                          //   icon: { icon: "event", color: getUproColor(categoriaSeleccionada === "uproDay" ? "uproGreen" : "uproBlue") },
                          //   onClick: () => {
                          //     handleCategoriaSeleccion("uproDay");
                          //   },
                          // },
                        ],
                        handleClose: () => { },
                        onClose: () => setExpandCategoriaActive(false),
                      });
                    }}
                  >
                    {categoriasDisplay[categoriaSeleccionada] || categoriaSeleccionada}&nbsp;
                    <Icon sx={{ rotate: expandCategoriaActive ? "180deg" : "0deg", transition: "all 0.5s" }}>
                      expand_more
                    </Icon>
                  </SoftButton>
                  <SoftButton
                    color="uproGreen"
                    circular
                    onClick={(event) => {
                      setExpandMedioPagoActive(true);
                      menu({
                        open: event.currentTarget,
                        align: "right",
                        options: [
                          {
                            name: "Todos los medios de pago",
                            icon: {
                              icon: "payments",
                              color: getUproColor(medioPagoSeleccionado === "todos" ? "uproGreen" : "uproBlue"),
                            },
                            onClick: () => {
                              setMedioPagoSeleccionado("todos");
                            },
                          },
                          ...mediosDePagoOptions.map((medioPago) => ({
                            name: medioPago.nombre,
                            icon: {
                              icon: "account_balance_wallet",
                              color: getUproColor(medioPagoSeleccionado === medioPago.id ? "uproGreen" : "uproBlue"),
                            },
                            onClick: () => {
                              setMedioPagoSeleccionado(medioPago.id);
                            },
                          })),
                        ],
                        handleClose: () => { },
                        onClose: () => setExpandMedioPagoActive(false),
                      });
                    }}
                  >
                    {mediosDePagoOptions.find((mp) => mp.id === medioPagoSeleccionado)?.nombre || "Medios de Pago"}&nbsp;
                    <Icon sx={{ rotate: expandMedioPagoActive ? "180deg" : "0deg", transition: "all 0.5s" }}>
                      expand_more
                    </Icon>
                  </SoftButton>
                  <SoftButton color="success" onClick={exportarExcel} disabled={loading || !reporteData}>
                    <Icon>download</Icon>&nbsp;Excel
                  </SoftButton>
                  <SoftButton
                    color="primary"
                    circular
                    onClick={(event) => {
                      setExpandActive(true);
                      menu({
                        open: event.currentTarget,
                        align: "right",
                        options: [
                          {
                            name: "Hoy",
                            icon: { icon: "calendar_today", color: getUproColor(fechaOption === "hoy" ? "uproGreen" : "uproBlue") },
                            onClick: () => {
                              setFechaOption("hoy");
                              setFechaInicio(dayjs().startOf("day").toDate());
                              setFechaFin(dayjs().endOf("day").toDate());
                              setExpandActive(false);
                            },
                          },
                          {
                            name: "Ayer",
                            icon: { icon: "event", color: getUproColor(fechaOption === "ayer" ? "uproGreen" : "uproBlue") },
                            onClick: () => {
                              setFechaOption("ayer");
                              setFechaInicio(dayjs().subtract(1, "day").startOf("day").toDate());
                              setFechaFin(dayjs().subtract(1, "day").endOf("day").toDate());
                              setExpandActive(false);
                            },
                          },
                          {
                            name: "Ultimos 7 días",
                            icon: { icon: "date_range", color: getUproColor(fechaOption === "Ultimos 7 días" ? "uproGreen" : "uproBlue") },
                            onClick: () => {
                              setFechaOption("Ultimos 7 días");
                              setFechaInicio(dayjs().subtract(7, "day").startOf("day").toDate());
                              setFechaFin(dayjs().endOf("day").toDate());
                              setExpandActive(false);
                            },
                          },
                          {
                            name: "Ultimos 30 días",
                            icon: { icon: "calendar_month", color: getUproColor(fechaOption === "Ultimos 30 días" ? "uproGreen" : "uproBlue") },
                            onClick: () => {
                              setFechaOption("Ultimos 30 días");
                              setFechaInicio(dayjs().subtract(30, "day").startOf("day").toDate());
                              setFechaFin(dayjs().endOf("day").toDate());
                              setExpandActive(false);
                            },
                          },
                          {
                            name: "Personalizado",
                            icon: { icon: "edit_calendar", color: getUproColor(fechaOption === "personalizado" ? "uproGreen" : "uproBlue") },
                            onClick: () => {
                              setFechaOption("personalizado");
                              setExpandActive(false);
                            },
                          },
                        ],
                        handleClose: () => { },
                        onClose: () => setExpandActive(false),
                      });
                    }}
                  >
                    {fechaOption}&nbsp;
                    <Icon sx={{ rotate: expandActive ? "180deg" : "0deg", transition: "all 0.5s" }}>
                      expand_more
                    </Icon>
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </Card>
        </SoftBox>

        {loading ? (
          <Card sx={{ width: "100%", minHeight: 400 }}>
            <Loading />
          </Card>
        ) : !reporteData || (Array.isArray(reporteData.resumen) && reporteData.resumen.length === 0) ? (
          <Card sx={{ width: "100%", minHeight: 400 }}>
            <SinDatos />
          </Card>
        ) : (
          <SoftBox width="100%">
            <ResumenEjecutivo />
            {productoFiltroAplicado && (
              <SoftTypography component="span" variant="h6" color="text" sx={{ fontWeight: "medium", whiteSpace: "nowrap", flex: { xs: "1 1 100%", md: "0 1 auto" } }} m={2}>
                Resultados para:&nbsp;
                <SoftTypography component="span" variant="h5" color="primary" fontWeight="bold">
                  {productoFiltroAplicado?.nombre ?? ""}
                </SoftTypography>
              </SoftTypography>
            )}
            <Card>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={tabValue}
                  onChange={(e, newValue) => {
                    if (categoriaSeleccionada === "cafeteria" && newValue === 1) return;
                    setTabValue(newValue);
                  }}
                >
                  <Tab label="Detallado por día" />
                  {categoriaSeleccionada !== "cafeteria" && <Tab label="Acumulado" disabled={categoriaSeleccionada === "cafeteria"} />}
                </Tabs>
              </Box>
              <SoftBox p={3}>
                {tabValue === 0 && <Reporte />}
                {tabValue === 1 && <TablaResumenAcumulado />}
              </SoftBox>
            </Card>
          </SoftBox>
        )}
      </SoftBox>

      {/* Modal para detalles por segmento */}
      <Modal
        open={modalSegmentoOpen}
        onClose={() => setModalSegmentoOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={modalSegmentoOpen}>
          <SoftBox
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              maxWidth: categoriaSeleccionada === "cafeteria" ? "1400px" : "1200px",
              maxHeight: "80vh",
              bgcolor: "background.paper",
              borderRadius: "12px",
              boxShadow: 24,
              p: 3,
              overflow: "auto"
            }}
          >
            <SoftBox display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
              {/* <SoftTypography variant="h5">
                Pedidos del segmento: {segmentoSeleccionado}
                {productoNombreSeleccionado && (
                  <SoftBox component="span" display="block" variant="body2" color="text" mt={0.5}>
                    Producto: {productoNombreSeleccionado}
                  </SoftBox>
                )}
              </SoftTypography> */}
              <IconButton
                size="small"
                color="inherit"
                variant="contained"
                onClick={() => setModalSegmentoOpen(false)}
              >
                <Tooltip title="Cerrar" placement="top">
                  <Icon color="dark">close</Icon>
                </Tooltip>
              </IconButton>
            </SoftBox>

            {loadingModal ? (
              <Loading />
            ) : (
              <DataTableCustom
                columns={[
                  {
                    field: "idPedido",
                    headerName: "ID",
                    width: "10%",
                    align: "center",
                    sortable: true
                  },
                  {
                    field: "cliente",
                    headerName: "CLIENTE",
                    width: "22%",
                    align: "center",
                    sortable: true
                  },
                  {
                    field: "app",
                    headerName: "APP",
                    width: "10%",
                    align: "center",
                    sortable: true
                  },
                  {
                    field: "estado",
                    headerName: "ESTADO",
                    width: "15%",
                    align: "center",
                    sortable: true,
                    renderCell: (item) => (
                      <SoftBox
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: "8px",
                          backgroundColor: item.estado === "CERRADO" ? "success.light" :
                            item.estado === "PENDIENTE" ? "warning.light" :
                              item.estado === "PAGO EN REVISIÓN" ? "info.light" :
                                item.estado === "RESERVADO" ? "secondary.light" :
                                  "info.light",
                          color: item.estado === "CERRADO" ? "success.dark" :
                            item.estado === "PENDIENTE" ? "warning.dark" :
                              item.estado === "PAGO EN REVISIÓN" ? "info.dark" :
                                item.estado === "RESERVADO" ? "secondary.dark" :
                                  "info.dark",
                          textAlign: "center",
                          fontSize: "0.75rem",
                          fontWeight: "medium"
                        }}
                      >
                        {item.estado}
                      </SoftBox>
                    )
                  },
                  ...(categoriaSeleccionada === "cafeteria" ? [
                    {
                      field: "mozo",
                      headerName: "MOZO",
                      width: "10%",
                      align: "center",
                      sortable: true
                    },
                    {
                      field: "cantidad",
                      headerName: "CANT.",
                      width: "8%",
                      align: "center",
                      sortable: true
                    }
                  ] : []),
                  {
                    field: "total",
                    headerName: "TOTAL",
                    width: "10%",
                    align: "right",
                    sortable: true,
                    customSortValue: (item) => parseFloat(item.total.replace(/[$,]/g, ''))
                  },
                  {
                    field: "costoUnit",
                    headerName: "COSTO UNIT.",
                    width: "10%",
                    align: "right",
                    sortable: true,
                    customSortValue: (item) => parseFloat((item.costoUnit || "$0").replace(/[$,]/g, ''))
                  },
                  {
                    field: "creado",
                    headerName: "CREADO",
                    width: "13%",
                    align: "center",
                    sortable: true
                  },
                  ...(categoriaSeleccionada === "cafeteria"
                    ? [
                      {
                        field: "mesa",
                        headerName: "MESA",
                        width: "10%",
                        align: "center",
                        sortable: true
                      }
                    ]
                    : [
                      {
                        field: "comentario",
                        headerName: "COMENTARIO",
                        width: "20%",
                        align: "left",
                        sortable: false
                      }
                    ]
                  ),
                ]}
                data={pedidosSegmento.map(pedido => ({
                  id: pedido.idPedido,
                  idPedido: pedido.idPedido,
                  cliente: pedido.cliente || "-",
                  app: pedido.app || "-",
                  estado: pedido.estado || "-",
                  ...(categoriaSeleccionada === "cafeteria" ? {
                    mozo: pedido.mozo || "-",
                    cantidad: pedido.cantidad || "-",
                    mesa: pedido.mesa || "-",
                  } : {}),
                  total: `$${formatMoneyPunto(pedido.total || 0)}`,
                  costoUnit: `$${formatMoneyPunto(pedido.costoUnit || 0)}`,
                  creado: pedido.creado ? dayjs(pedido.creado).format("DD/MM/YYYY HH:mm") : "-",
                  ...(categoriaSeleccionada !== "cafeteria" ? { comentario: pedido.comentario || "-" } : {})
                }))}
                loading={loadingModal}
                showActions={false}
                emptyMessage="No hay pedidos para mostrar"
                maxHeight="400px"
                sortable={true}
                defaultSort={{ field: "creado", direction: "desc" }}
                footerContent={
                  <SoftBox display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <SoftTypography variant="caption" color="text">
                      Total de pedidos: {pedidosSegmento?.length || 0}
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text" fontWeight="bold">
                      Segmento: {segmentoSeleccionado}
                    </SoftTypography>
                    {segmentoCostoUnitSeleccionado !== null && segmentoCostoUnitSeleccionado !== undefined && (
                      <SoftTypography variant="caption" color="text" fontWeight="bold">
                        Costo unitario filtrado: ${formatMoneyPunto(num(segmentoCostoUnitSeleccionado))}
                      </SoftTypography>
                    )}
                  </SoftBox>
                }
              />
            )}
          </SoftBox>
        </Fade>
      </Modal>

      {/* Modal para detalles por producto */}
      <Modal
        open={modalProductoOpen}
        onClose={() => setModalProductoOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={modalProductoOpen}>
          <SoftBox
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              maxWidth: categoriaSeleccionada === "cafeteria" ? "1400px" : "1200px",
              maxHeight: "80vh",
              bgcolor: "background.paper",
              borderRadius: "12px",
              boxShadow: 24,
              p: 3,
              overflow: "auto"
            }}
          >
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <SoftTypography variant="subtitle1" fontWeight="bold">
                Pedidos del producto: {productoSeleccionado}
                {productoCostoUnitSeleccionado !== null && productoCostoUnitSeleccionado !== undefined ? ` (Costo unit.: $${formatMoneyPunto(num(productoCostoUnitSeleccionado))})` : ""}
              </SoftTypography>
              {/* <SoftButton
                variant="outlined"
                color="secondary"
                onClick={() => setModalProductoOpen(false)}
                startIcon={<Icon>close</Icon>}
              >
                Cerrar
              </SoftButton> */}
              <IconButton
                size="small"
                color="inherit"
                variant="contained"
                onClick={() => setModalProductoOpen(false)}
              >
                <Tooltip title="Cerrar" placement="top">
                  <Icon color="dark">close</Icon>
                </Tooltip>
              </IconButton>
            </SoftBox>

            {loadingModal ? (
              <Loading />
            ) : (
              <DataTableCustom
                columns={[
                  {
                    field: "idPedido",
                    headerName: "ID",
                    width: "10%",
                    align: "center",
                    sortable: true
                  },
                  {
                    field: "cliente",
                    headerName: "CLIENTE",
                    width: "25%",
                    align: "left",
                    sortable: true
                  },
                  {
                    field: "estado",
                    headerName: "ESTADO",
                    width: "15%",
                    align: "center",
                    sortable: true,
                    renderCell: (item) => (
                      <SoftBox
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: "8px",
                          backgroundColor: item.estado === "CERRADO" ? "success.light" :
                            item.estado === "PENDIENTE" ? "warning.light" :
                              item.estado === "PAGO EN REVISIÓN" ? "info.light" :
                                item.estado === "RESERVADO" ? "secondary.light" :
                                  "info.light",
                          color: item.estado === "CERRADO" ? "success.dark" :
                            item.estado === "PENDIENTE" ? "warning.dark" :
                              item.estado === "PAGO EN REVISIÓN" ? "info.dark" :
                                item.estado === "RESERVADO" ? "secondary.dark" :
                                  "info.dark",
                          textAlign: "center",
                          fontSize: "0.75rem",
                          fontWeight: "medium"
                        }}
                      >
                        {item.estado}
                      </SoftBox>
                    )
                  },
                  ...(categoriaSeleccionada === "cafeteria" ? [
                    {
                      field: "mozo",
                      headerName: "MOZO",
                      width: "10%",
                      align: "center",
                      sortable: true
                    },
                    {
                      field: "cantidad",
                      headerName: "CANT.",
                      width: "8%",
                      align: "center",
                      sortable: true
                    }
                  ] : []),
                  {
                    field: "total",
                    headerName: "TOTAL",
                    width: "10%",
                    align: "right",
                    sortable: true,
                    customSortValue: (item) => parseFloat(item.total.replace(/[$,]/g, ''))
                  },
                  {
                    field: "costoUnit",
                    headerName: "COSTO UNIT.",
                    width: "10%",
                    align: "right",
                    sortable: true,
                    customSortValue: (item) => parseFloat((item.costoUnit || "$0").replace(/[$,]/g, ''))
                  },
                  {
                    field: "creado",
                    headerName: "FECHA",
                    width: "13%",
                    align: "center",
                    sortable: true
                  },
                  ...(categoriaSeleccionada === "cafeteria"
                    ? [
                      {
                        field: "mesa",
                        headerName: "MESA",
                        width: "12%",
                        align: "center",
                        sortable: true
                      }
                    ]
                    : [
                      {
                        field: "comentario",
                        headerName: "COMENTARIO",
                        width: "17%",
                        align: "left",
                        sortable: false
                      }
                    ]
                  )
                ]}
                data={pedidosProducto.map(pedido => ({
                  id: pedido.idPedido,
                  idPedido: pedido.idPedido,
                  cliente: pedido.cliente || "-",
                  estado: pedido.estado || "-",
                  ...(categoriaSeleccionada === "cafeteria" ? {
                    mozo: pedido.mozo || "-",
                    cantidad: pedido.cantidad || "-",
                    mesa: pedido.mesa || "-",
                  } : {}),
                  total: `$${formatMoneyPunto(pedido.total || 0)}`,
                  costoUnit: `$${formatMoneyPunto(pedido.costoUnit || 0)}`,
                  creado: pedido.creado ? dayjs(pedido.creado).format("DD/MM/YYYY HH:mm") : "-",
                  ...(categoriaSeleccionada !== "cafeteria" ? { comentario: pedido.comentario || "-" } : {})
                }))}
                loading={loadingModal}
                showActions={false}
                emptyMessage="No hay pedidos para mostrar"
                maxHeight="400px"
                sortable={true}
                defaultSort={{ field: "creado", direction: "desc" }}
                footerContent={
                  <SoftBox display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <SoftTypography variant="caption" color="text">
                      Total de pedidos: {pedidosProducto?.length || 0}
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text" fontWeight="bold">
                      Producto: {productoSeleccionado}
                    </SoftTypography>
                  </SoftBox>
                }
              />
            )}
          </SoftBox>
        </Fade>
      </Modal>
    </DashboardLayout>
  );
}

export default AnalisisIngresosCostos;

